(() => {
  'use strict';
  if (window.TodayMerchArtworkImagePreparation) return;

  const VERSION = 'reversible-artwork-preparation-v4';
  const MAX_COLORS = 16;
  const MAX_EDITABLE_COLORS = window.TodayMerchArtworkPaletteAdapter?.MAX_EDITABLE_COLORS || 32;
  const MAX_BYTES = 10 * 1024 * 1024;
  const MAX_OUTPUT_BYTES = 1500000;
  const acceptedReceipts = new WeakSet();
  const acceptedPalettes = new WeakMap();
  const acceptedPreparations = new WeakMap();
  const appliedPreparations = new WeakMap();
  const automaticOriginals = new WeakMap();
  const appliedReceipts = new WeakMap();
  const automaticReceipts = new WeakSet();
  const compatibleAnalyses = new WeakMap();
  const fileDigests = new WeakMap();
  const preparedArtifacts = new Set();
  const MAX_CACHED_ARTIFACTS = 6;
  const MAX_CACHED_BYTES = 24 * 1024 * 1024;
  const fail = (code, message) => Object.assign(new Error(message), { code });
  const abortError = () => new DOMException('La proposition a été annulée.', 'AbortError');
  const active = signal => { if (signal?.aborted) throw abortError(); };
  const plain = value => value && Object.getPrototypeOf(value) === Object.prototype;
  function freeze(value) {
    if (Array.isArray(value) || plain(value)) {
      Object.values(value).forEach(freeze); Object.freeze(value);
    }
    return value;
  }
  function copy(value, depth = 0) {
    if (depth > 8) throw fail('INVALID_CONTEXT', 'Le contexte est trop complexe.');
    if (value == null) return null;
    if (typeof value === 'boolean' || typeof value === 'number' && Number.isFinite(value)) return value;
    if (typeof value === 'string' && value.length <= 4096) return value;
    if (Array.isArray(value) && value.length <= 100) return value.map(item => copy(item, depth + 1));
    if (plain(value) && Object.keys(value).length <= 50) {
      return Object.fromEntries(Object.keys(value).sort().map(key => [key, copy(value[key], depth + 1)]));
    }
    throw fail('INVALID_CONTEXT', 'Le contexte de la proposition est invalide.');
  }
  const canonical = value => JSON.stringify(copy(value));
  // A decoder/encoder may finish after cancellation or fail to call back. The
  // session settles promptly; a late output remains isolated and unapplied.
  function bounded(promise, signal, timeoutMs = 30000) {
    return new Promise((resolve, reject) => {
      let done = false;
      const finish = (failure, value) => {
        if (done) return; done = true; clearTimeout(timer); signal.removeEventListener('abort', onAbort);
        failure ? reject(failure) : resolve(value);
      };
      const onAbort = () => finish(abortError());
      const timer = setTimeout(() => finish(fail('PREPARATION_TIMEOUT', 'La préparation a pris trop de temps. Votre fichier reste inchangé.')), timeoutMs);
      signal.addEventListener('abort', onAbort, { once: true });
      promise.then(value => finish(null, value), failure => finish(failure));
      if (signal.aborted) onAbort();
    });
  }
  async function digest(value, signal) {
    active(signal);
    if (!globalThis.crypto?.subtle) throw fail('HASH_UNAVAILABLE', 'La vérification du fichier est indisponible.');
    const calculate = async () => {
      const bytes = value instanceof Blob ? await value.arrayBuffer() : new TextEncoder().encode(value);
      const result = await crypto.subtle.digest('SHA-256', bytes);
      return Array.from(new Uint8Array(result), byte => byte.toString(16).padStart(2, '0')).join('');
    };
    let task = value instanceof File ? fileDigests.get(value) : null;
    if (!task) {
      task = calculate();
      if (value instanceof File) {
        fileDigests.set(value, task);
        task.catch(() => { if (fileDigests.get(value) === task) fileDigests.delete(value); });
      }
    }
    const result = signal ? await bounded(task, signal) : await task;
    active(signal);
    return result;
  }
  function fileValid(file) {
    if (!(file instanceof File) || !file.size || file.size > MAX_BYTES) {
      throw fail('FILE_TOO_LARGE_OR_INVALID', 'Ce fichier ne peut pas être préparé localement. L’original est conservé.');
    }
  }
  function contextFrom(readContext) {
    const current = readContext();
    if (!current || typeof current.key !== 'string' || !current.key.length || current.key.length > 4096) {
      throw fail('INVALID_CONTEXT', 'Le contexte de personnalisation est indisponible.');
    }
    fileValid(current.originalFile); fileValid(current.effectiveFile);
    const binding = { key: current.key };
    for (const key of ['productId', 'variantId', 'supplier', 'techniqueId', 'positionId', 'zoneKey', 'artworkId', 'artworkRevision',
      'preparationRevision', 'rulesRevision', 'configurationRevision', 'capabilityRevision', 'priceRevision',
      'capabilitiesValidUntil', 'dimensionsMm', 'placement']) binding[key] = copy(current[key]);
    return { originalFile: current.originalFile, effectiveFile: current.effectiveFile, binding: freeze(binding) };
  }
  function equalContext(left, right) {
    return left.originalFile === right.originalFile && left.effectiveFile === right.effectiveFile
      && canonical(left.binding) === canonical(right.binding);
  }
  function automaticCommitBinding(binding) {
    const derived = new Set(['contentFrame', 'imageNaturalRatio', 'artworkRatio', 'imageCropTop', 'imageCropRight', 'imageCropBottom', 'imageCropLeft']);
    const stable = Object.fromEntries(Object.entries(binding).filter(([key]) => !['key', 'preparationRevision', 'dimensionsMm'].includes(key)));
    if (plain(stable.placement)) stable.placement = { ...Object.fromEntries(Object.entries(stable.placement).filter(([key]) => !derived.has(key))),
      contentFrameUserClip: stable.placement.contentFrame?.userClip ?? null };
    return stable;
  }
  function planFrom(plan) {
    if (!plain(plan)) throw fail('INVALID_PLAN', 'Choisissez les modifications à comparer.');
    const allowed = ['background', 'palette', 'mapping', 'mappingMaxColors', 'inkReferences', 'restoreOriginal', 'treatment', 'source'];
    if (Object.keys(plan).some(key => !allowed.includes(key))) throw fail('INVALID_PLAN', 'Une modification n’est pas reconnue.');
    if (plan.restoreOriginal === true) {
      if (Object.keys(plan).length !== 1) throw fail('INVALID_PLAN', 'Le retour à l’original ne peut pas ajouter une modification.');
      return freeze({ restoreOriginal: true });
    }
    const result = { background: plan.background ?? 'keep', restoreOriginal: false };
    if (!['keep', 'remove'].includes(result.background) || plan.restoreOriginal != null && plan.restoreOriginal !== false) {
      throw fail('INVALID_PLAN', 'Choisissez si le fond doit être conservé.');
    }
    if (plan.source != null) {
      if (!['original', 'effective'].includes(plan.source)) throw fail('INVALID_PLAN', 'Choisissez le fichier à préparer.');
      result.source = plan.source;
    }
    if (plan.treatment != null) {
      if (!['full_color', 'monochrome'].includes(plan.treatment) || ['palette', 'mapping', 'mappingMaxColors', 'inkReferences'].some(key => plan[key] != null)) {
        throw fail('INVALID_PLAN', 'Choisissez un seul traitement pour la proposition.');
      }
      result.treatment = plan.treatment;
    }
    if (plan.palette != null) {
      if (!plain(plan.palette) || Object.keys(plan.palette).some(key => !['maxColors', 'jpegEdgeCleanup'].includes(key))
        || !Number.isInteger(plan.palette.maxColors) || plan.palette.maxColors < 1 || plan.palette.maxColors > MAX_COLORS
        || plan.palette.jpegEdgeCleanup != null && typeof plan.palette.jpegEdgeCleanup !== 'boolean') {
        throw fail('INVALID_PLAN', 'La proposition de palette est invalide.');
      }
      result.palette = { maxColors: plan.palette.maxColors, jpegEdgeCleanup: plan.palette.jpegEdgeCleanup === true };
    }
    if (plan.mapping != null) {
      if (!plain(plan.mapping) || !Object.keys(plan.mapping).length || Object.keys(plan.mapping).length > MAX_EDITABLE_COLORS
        || Object.entries(plan.mapping).some(([key, value]) => !/^#[a-f\d]{6}$/i.test(key)
          || value !== null && (typeof value !== 'string' || !/^#[a-f\d]{6}$/i.test(value)))) throw fail('INVALID_PLAN', 'Les substitutions sont invalides.');
      result.mapping = copy(plan.mapping);
    }
    if (plan.mappingMaxColors != null) {
      if (!plan.mapping || !Number.isInteger(plan.mappingMaxColors) || plan.mappingMaxColors < 1 || plan.mappingMaxColors > MAX_EDITABLE_COLORS) {
        throw fail('INVALID_PLAN', 'La limite de couleurs après modification est invalide.');
      }
      result.mappingMaxColors = plan.mappingMaxColors;
    }
    if (plan.inkReferences != null) {
      if (!Array.isArray(plan.inkReferences) || plan.inkReferences.length > MAX_EDITABLE_COLORS) throw fail('INVALID_PLAN', 'Les références d’encre sont invalides.');
      result.inkReferences = copy(plan.inkReferences);
    }
    return freeze(result);
  }
  async function encode(pixels, width, height, file, signal) {
    const canvas = document.createElement('canvas'); canvas.width = width; canvas.height = height;
    try {
      const context = canvas.getContext('2d');
      if (!context) throw fail('CANVAS_UNAVAILABLE', 'La préparation locale est indisponible.');
      context.putImageData(new ImageData(new Uint8ClampedArray(pixels), width, height), 0, 0);
      const blob = await new Promise(resolve => canvas.toBlob(resolve, 'image/png'));
      active(signal);
      if (!blob?.size || blob.size > MAX_OUTPUT_BYTES) throw fail('OUTPUT_TOO_LARGE', 'Cette proposition nécessite une préparation par notre équipe.');
      return new File([blob], `${file.name.replace(/\.[^.]+$/, '') || 'design'}-fond.png`, { type: 'image/png' });
    } finally { canvas.width = canvas.height = 0; }
  }
  async function prepareFile(base, original, plan, signal) {
    if (plan.restoreOriginal) return { file: original, transformations: [{ type: 'restore_original' }],
      warnings: [], inkReferences: [], dimensions: null, palette: null };
    let file = base, dimensions = null, palette = null;
    const transformations = [], warnings = [];
    const adapter = window.TodayMerchArtworkPaletteAdapter;
    if (!adapter?.decodePixels) throw fail('DECODER_UNAVAILABLE', 'La préparation locale est indisponible.');
    if (plan.background === 'remove') {
      if (!window.TodayMerchLogoBackground?.prepareArtworkPixels) throw fail('BACKGROUND_UNAVAILABLE', 'Le détourage local est indisponible.');
      const decoded = await adapter.decodePixels(file, { maxSide: adapter.MAX_SIDE, allowPartialTransparency: true, signal });
      const result = await window.TodayMerchLogoBackground.prepareArtworkPixels(decoded, { background: 'remove', signal });
      active(signal);
      if (result.width !== decoded.width || result.height !== decoded.height || result.pixels?.length !== decoded.pixels.length) {
        throw fail('INVALID_RESULT', 'Le détourage ne conserve pas les dimensions de votre visuel.');
      }
      if (!result.changed) throw fail('BACKGROUND_UNCERTAIN', 'Aucun fond simple n’a pu être retiré. Votre fichier reste inchangé.');
      file = await encode(result.pixels, result.width, result.height, base, signal);
      dimensions = { width: result.width, height: result.height };
      transformations.push({ type: 'remove_background', method: 'connected_border', backgroundIntent: 'unknown', diagnostics: copy(result.diagnostics) });
      warnings.push('WHITE_BACKGROUND_INTENT_UNKNOWN', ...(result.warnings || []));
    }
    if (plan.treatment) {
      if (!adapter.prepareTreatment) throw fail('TREATMENT_UNAVAILABLE', 'Le traitement du visuel est indisponible.');
      const result = await adapter.prepareTreatment(file, { treatment: plan.treatment, signal });
      active(signal);
      if (!(result.file instanceof File) || result.file.type !== 'image/png' || result.scaled !== false || result.productionReady !== false
        || result.requiresApproval !== true || result.alphaPreserved !== true || result.width !== result.sourceWidth || result.height !== result.sourceHeight) {
        throw fail('INVALID_RESULT', 'La proposition de traitement nécessite une vérification.');
      }
      file = result.file; palette = result.effectivePalette;
      dimensions = { width: result.width, height: result.height };
      const operation = { type: plan.treatment === 'monochrome' ? 'monochrome_silhouette' : 'rasterize_full_color',
        method: result.method, sourceFormat: result.sourceFormat, alphaPreserved: true, backgroundRemoved: false };
      if (plan.treatment === 'monochrome') Object.assign(operation, { color: '#000000', coverage: copy(result.coverage), whitePaintIncluded: result.whitePaintIncluded });
      transformations.push(operation);
      warnings.push(...(result.warnings || []));
    }
    if (plan.palette) {
      const result = await adapter.prepare(file, { ...plan.palette, signal });
      active(signal);
      if (!(result.file instanceof File) || result.scaled !== false || result.productionReady !== false
        || result.requiresApproval !== true || result.width !== result.sourceWidth || result.height !== result.sourceHeight) {
        throw fail('INVALID_RESULT', 'La proposition de palette nécessite une vérification.');
      }
      file = result.file; palette = result.effectivePalette;
      dimensions = { width: result.width, height: result.height };
      transformations.push({ type: 'reduce_palette', method: result.method, ...plan.palette,
        palette: copy(palette), changedPixelRatio: result.changedPixelRatio, changedOpaquePixelCount: result.changedOpaquePixelCount ?? null, whitePaintPreserved: result.whitePaintPreserved ?? null });
      warnings.push(...(result.warnings || []));
    }
    if (plan.mapping) {
      const result = await adapter.remap(file, plan.mapping, { maxColors: plan.mappingMaxColors || plan.palette?.maxColors || MAX_EDITABLE_COLORS, signal });
      active(signal);
      if (!(result.file instanceof File) || result.scaled !== false || result.productionReady !== false
        || result.requiresApproval !== true || result.width !== result.sourceWidth || result.height !== result.sourceHeight) {
        throw fail('INVALID_RESULT', 'Les substitutions nécessitent une vérification.');
      }
      file = result.file; palette = result.effectivePalette;
      dimensions = { width: result.width, height: result.height };
      transformations.push({ type: 'replace_or_merge_colors', method: result.method, mapping: copy(result.mapping), palette: copy(palette),
        sourcePalette: copy(result.sourcePalette), sourceFormat: result.sourceFormat,
        vectorPreserved: result.vectorPreserved === true,
        ...(typeof result.requiresInkReview === 'boolean' ? { requiresInkReview: result.requiresInkReview } : {}),
        ...(typeof result.inkSeparationVerified === 'boolean' ? { inkSeparationVerified: result.inkSeparationVerified } : {}),
        ...(typeof result.inkReviewReason === 'string' ? { inkReviewReason: result.inkReviewReason } : {}),
        removedPixelCount: result.removedPixelCount || 0, alphaPreserved: result.alphaPreserved === true });
      warnings.push(...(result.warnings || []));
    }
    let inkReferences = [];
    if (plan.inkReferences?.length) {
      if (!adapter.validateInkReferences || !palette) throw fail('INK_REFERENCE_UNAVAILABLE', 'Préparez une palette avant de renseigner les références d’encre.');
      inkReferences = adapter.validateInkReferences(palette, plan.inkReferences);
      transformations.push({ type: 'declare_ink_references', references: copy(inkReferences) });
      warnings.push('INK_REFERENCES_UNVERIFIED');
    }
    if (!transformations.length) throw fail('NO_TRANSFORMATION', 'Choisissez une modification à comparer.');
    if (base.type === 'image/svg+xml' && file.type !== base.type) warnings.push('VECTOR_RASTERIZED');
    return { file, dimensions, palette, transformations, warnings: [...new Set(warnings)], inkReferences };
  }

  function retainArtifact(entry) {
    if (!preparedArtifacts.has(entry)) return;
    preparedArtifacts.delete(entry); preparedArtifacts.add(entry);
    let bytes = [...preparedArtifacts].reduce((sum, item) => sum + item.bytes, 0);
    while (preparedArtifacts.size > MAX_CACHED_ARTIFACTS || bytes > MAX_CACHED_BYTES) {
      const oldest = preparedArtifacts.values().next().value;
      if (!oldest) break;
      preparedArtifacts.delete(oldest); bytes -= oldest.bytes;
      // Eviction releases cache ownership only. A live dialog still owns its
      // lease and may finish; speculative or cancelled consumers cannot kill it.
    }
  }
  function preparedArtifact(originalFile, effectiveFile, plan, signal) {
    active(signal); fileValid(originalFile); fileValid(effectiveFile);
    const adapter = window.TodayMerchArtworkPaletteAdapter;
    const background = plan.background === 'remove' ? window.TodayMerchLogoBackground : null;
    const key = canonical({ engine: VERSION, adapter: adapter?.VERSION || null,
      background: background?.VERSION || null, plan: { ...plan, source: plan.source || 'effective' } });
    let entry = [...preparedArtifacts].find(item => item.originalFile === originalFile && item.effectiveFile === effectiveFile
      && item.adapter === adapter && item.background === background && item.key === key && !item.controller.signal.aborted);
    if (!entry) {
      const controller = new AbortController();
      entry = { originalFile, effectiveFile, adapter, background, key, controller, consumers: new Set(), state: 'pending',
        bytes: originalFile.size + (effectiveFile === originalFile ? 0 : effectiveFile.size) };
      preparedArtifacts.add(entry);
      entry.promise = bounded(Promise.resolve().then(async () => {
        const internalSignal = controller.signal;
        active(internalSignal);
        const baseFile = plan.source === 'original' ? originalFile : effectiveFile;
        const [originalSha256, baseSha256, result] = await Promise.all([
          digest(originalFile, internalSignal), digest(baseFile, internalSignal),
          prepareFile(baseFile, originalFile, plan, internalSignal),
        ]);
        active(internalSignal); fileValid(result.file);
        const effectiveSha256 = await digest(result.file, internalSignal);
        active(internalSignal);
        return freeze({ baseFile, originalSha256, baseSha256, effectiveSha256, result });
      }), controller.signal).then(value => {
        active(controller.signal);
        entry.state = 'ready';
        // Prepared palette files retain one byte per pixel of exact ink
        // evidence in the adapter, in addition to their compressed bytes.
        entry.bytes += value.result.file.size + (value.result.dimensions?.width || 0) * (value.result.dimensions?.height || 0);
        retainArtifact(entry);
        return value;
      }, error => {
        entry.state = 'error'; preparedArtifacts.delete(entry); controller.abort(); throw error;
      });
      // A final lease may leave before an uncooperative decoder settles.
      entry.promise.catch(() => {});
    }
    retainArtifact(entry);
    const token = {};
    entry.consumers.add(token);
    return new Promise((resolve, reject) => {
      let done = false;
      const finish = (error, value) => {
        if (done) return; done = true;
        signal?.removeEventListener('abort', onAbort);
        entry.consumers.delete(token);
        if (!entry.consumers.size && entry.state === 'pending') {
          preparedArtifacts.delete(entry); entry.controller.abort();
        }
        error ? reject(error) : resolve(value);
      };
      const onAbort = () => finish(abortError());
      signal?.addEventListener('abort', onAbort, { once: true });
      entry.promise.then(value => finish(null, value), error => finish(error));
      if (signal?.aborted) onAbort();
    });
  }
  async function prewarm({ originalFile, effectiveFile, plan } = {}, { signal } = {}) {
    active(signal); fileValid(originalFile); fileValid(effectiveFile);
    await preparedArtifact(originalFile, effectiveFile, planFrom(plan), signal);
    active(signal);
    // Prepared bytes are not a proposal, consent, or a production selection.
    return Object.freeze({ status: 'ready', productionReady: false });
  }

  async function analyzeCompatible(file, { signal } = {}) {
    fileValid(file); active(signal);
    const analyzer = window.TodayMerchMidoceanArtworkFeasibility;
    if (typeof analyzer?.analyzeFile !== 'function') throw fail('ANALYSIS_UNAVAILABLE', 'L’analyse des couleurs est indisponible.');
    const version = `${analyzer.VERSION || ''}:${analyzer.ANALYSIS_VERSION || ''}`;
    const result = await bounded(Promise.resolve().then(() => analyzer.analyzeFile(file, { signal })), signal || new AbortController().signal);
    active(signal);
    if (!plain(result)) throw fail('ANALYSIS_UNAVAILABLE', 'L’analyse des couleurs est indisponible.');
    const analysis = freeze(structuredClone(result));
    compatibleAnalyses.set(analysis, { file, analyzer, version });
    return analysis;
  }

  function compatibleApplication(chosen, displayed, analysis, maxColors) {
    const provenance = compatibleAnalyses.get(analysis), analyzer = window.TodayMerchMidoceanArtworkFeasibility;
    const plan = chosen.manifest.plan, operations = chosen.manifest.transformations;
    const operation = operations[0], palette = operation?.palette;
    const measured = analysis?.effectivePalette;
    const samePalette = (left, right) => Array.isArray(left) && Array.isArray(right)
      && left.length === right.length && left.every(color => typeof color === 'string' && /^#[a-f\d]{6}$/i.test(color))
      && new Set(left.map(color => color.toLowerCase())).size === left.length
      && left.map(color => color.toLowerCase()).sort().join(',') === right.map(color => String(color).toLowerCase()).sort().join(',');
    if (displayed !== chosen.publicProposal) throw fail('PROPOSAL_MISMATCH', 'La version ne correspond plus à la proposition.');
    if (!provenance || provenance.file !== chosen.publicProposal.baseFile || provenance.analyzer !== analyzer
      || provenance.version !== `${analyzer?.VERSION || ''}:${analyzer?.ANALYSIS_VERSION || ''}`
      || !Number.isInteger(maxColors) || maxColors < 1 || maxColors > MAX_COLORS
      || analysis.status !== 'detected' || analysis.confidence !== 'high' || analysis.verified !== true
      || analysis.classification?.kind !== 'flat_colors' || analysis.classification?.gradients !== 'absent'
      || analysis.classification?.verified !== true || analysis.classification?.background !== 'preserved'
      || analysis.classification?.transparency === 'partial' || analysis.partialTransparency === true || analysis.gradients === true
      || !Number.isInteger(analysis.colorCount) || analysis.colorCount < 1 || analysis.colorCount > maxColors
      || !samePalette(measured, analysis.palette) || !samePalette(palette, measured) || palette.length !== analysis.colorCount
      || !plan.palette || plan.palette.maxColors < analysis.colorCount || plan.palette.maxColors > maxColors
      || plan.palette.jpegEdgeCleanup !== false || plan.background !== 'keep' || plan.source === 'original'
      || plan.mapping || plan.treatment || plan.restoreOriginal || plan.inkReferences
      || operations.length !== 1 || operation.type !== 'reduce_palette' || operation.changedPixelRatio !== 0
      || chosen.manifest.warnings.some(warning => warning !== 'VECTOR_RASTERIZED')) {
      throw fail('AUTOMATIC_APPLICATION_UNSAFE', 'Ce visuel nécessite une validation explicite de son adaptation.');
    }
    return { mode: 'automatic_compatible', authority: 'local_technical_only', maxColors,
      colorCount: palette.length, palette: palette.slice(), analysisVersion: analysis.analysisVersion || null,
      originalSha256: chosen.manifest.original.sha256, effectiveSha256: chosen.manifest.effective.sha256,
      proposalSha256: chosen.proposalSha256 };
  }

  // This session owns proposals only. The existing selector owns the effective
  // file and revisions; readContext is read afresh before every state change.
  function createSession({ readContext, commit } = {}) {
    if (typeof readContext !== 'function' || typeof commit !== 'function' || commit.constructor?.name === 'AsyncFunction') {
      throw fail('INVALID_INTEGRATION', 'La lecture et l’application doivent être synchrones.');
    }
    let disposed = false, sequence = 0, job = null, proposal = null, status = 'idle', error = null;
    const ensureAlive = () => { if (disposed) throw fail('SESSION_CLOSED', 'La comparaison est fermée.'); };
    function clear(nextStatus) {
      job?.controller.abort(); job?.cleanup(); job = null; proposal = null; ++sequence; status = nextStatus; error = null;
    }
    function current(captured) {
      ensureAlive();
      if (!equalContext(captured, contextFrom(readContext))) throw fail('STALE_CONTEXT', 'Votre design ou votre configuration a changé. Préparez une nouvelle proposition.');
    }
    async function propose(input, options = {}) {
      ensureAlive(); clear('preparing');
      const controller = new AbortController(), generation = sequence;
      const externalAbort = () => { if (job?.controller === controller) clear('cancelled'); else controller.abort(); };
      job = { controller, cleanup: () => options.signal?.removeEventListener('abort', externalAbort) };
      options.signal?.addEventListener('abort', externalAbort, { once: true });
      if (options.signal?.aborted) externalAbort();
      const signal = controller.signal;
      try {
        active(signal);
        const context = contextFrom(readContext), plan = planFrom(input);
        current(context); active(signal);
        const { baseFile, originalSha256, baseSha256, effectiveSha256, result } = await preparedArtifact(
          context.originalFile, context.effectiveFile, plan, signal);
        current(context); active(signal);
        const original = { sha256: originalSha256, fileName: context.originalFile.name, mimeType: context.originalFile.type, byteLength: context.originalFile.size };
        const base = { sha256: baseSha256, fileName: baseFile.name, mimeType: baseFile.type, byteLength: baseFile.size };
        const effective = { sha256: effectiveSha256, fileName: result.file.name, mimeType: result.file.type, byteLength: result.file.size };
        const manifest = freeze({ schemaVersion: 1, method: VERSION, proposalId: crypto.randomUUID(), proposalRevision: generation,
          original, base, effective, context: context.binding, plan, transformations: copy(result.transformations),
          warnings: result.warnings, inkReferences: copy(result.inkReferences), dimensionsPx: result.dimensions,
          productionReady: false, analysisStatus: 'unverified', supplierReadiness: 'unknown' });
        const proposalSha256 = await digest(canonical(manifest), signal);
        current(context); active(signal);
        if (generation !== sequence) throw abortError();
        const publicProposal = freeze({ proposalId: manifest.proposalId, proposalSha256, file: result.file, originalFile: context.originalFile,
          baseFile, manifest, palette: copy(result.palette), requiresApproval: true, productionReady: false });
        proposal = { context, file: result.file, manifest, proposalSha256, publicProposal };
        status = 'ready';
        return publicProposal;
      } catch (failure) {
        controller.abort();
        if (generation === sequence) {
          proposal = null; status = failure.name === 'AbortError' ? 'cancelled' : failure.code === 'STALE_CONTEXT' ? 'stale' : 'error';
          error = { code: failure.code || failure.name, message: failure.message }; job?.cleanup(); job = null;
        }
        throw failure;
      }
    }
    function apply(displayed = {}, automatic = null) {
      const { proposalId, proposalSha256 } = displayed;
      ensureAlive();
      if (!proposal || status !== 'ready') throw fail('NO_PROPOSAL', 'Aucune proposition ne peut être utilisée.');
      const chosen = proposal;
      try { current(chosen.context); } catch (failure) { clear('stale'); throw failure; }
      if (proposalId !== chosen.manifest.proposalId || proposalSha256 !== chosen.proposalSha256) {
        throw fail('PROPOSAL_MISMATCH', 'La version affichée ne correspond plus à la proposition.');
      }
      const application = automatic && compatibleApplication(chosen, displayed, automatic.analysis, automatic.maxColors);
      const receipt = freeze({ ...chosen.manifest, proposalSha256, ...(application ? { appliedAt: new Date().toISOString(), application, consent: null } : { acceptedAt: new Date().toISOString(),
        consent: { scope: 'exact_artwork_adaptation', action: chosen.manifest.plan.restoreOriginal ? 'restore_original' : 'use_this_version',
          originalSha256: chosen.manifest.original.sha256, effectiveSha256: chosen.manifest.effective.sha256,
          proposalSha256, authority: 'client_pending_server_verification' } }) });
      // This verified pixel equivalence belongs to the generated File even if
      // applying its preview changes geometry and invalidates the placement.
      // It never supplies application, supplier, price or production authority.
      const originalEquivalent = !!application && (chosen.publicProposal.baseFile === chosen.context.originalFile
        || automaticOriginals.get(chosen.publicProposal.baseFile) === chosen.context.originalFile);
      if (originalEquivalent) automaticOriginals.set(chosen.file, chosen.context.originalFile);
      else automaticOriginals.delete(chosen.file);
      const kind = chosen.manifest.plan.restoreOriginal ? 'original' : chosen.manifest.plan.palette || chosen.manifest.plan.mapping ? 'palette' : 'adapted';
      // The adapter can return its input File for an unchanged palette. Keep
      // the immutable prior receipt before replacing the File's latest proof.
      const baseApplication = appliedPreparations.get(chosen.publicProposal.baseFile);
      const baseReceipt = baseApplication?.originalFile === chosen.context.originalFile
        && baseApplication.effectiveFile === chosen.publicProposal.baseFile ? baseApplication.receipt : null;
      status = 'committing';
      try {
        const result = commit({ originalFile: chosen.context.originalFile, effectiveFile: chosen.file, kind, receipt });
        if (result && typeof result.then === 'function') throw fail('ASYNC_COMMIT_UNSUPPORTED', 'L’application de cette version doit être atomique.');
        if (result !== true) throw fail('COMMIT_REJECTED', 'Votre version n’a pas été appliquée. Réessayez après vérification.');
      } catch (failure) {
        if (proposal === chosen && !disposed) {
          try { current(chosen.context); status = 'ready'; } catch { clear('stale'); }
        }
        throw failure;
      }
      clear(application ? 'applied' : 'accepted');
      if (!application) {
        acceptedReceipts.add(receipt);
        // A later automatic application must not bridge a manual acceptance,
        // including an accepted operation that preserves the same File.
        appliedPreparations.delete(chosen.file);
      }
      if (application) {
        let appliedContext = null;
        try {
          const after = contextFrom(readContext);
          // Exact pixel-equivalent output can temporarily lose decoded preview
          // bounds when its URL is replaced. Preserve the file transition only:
          // user placement and scope stay exact, and missing measurements stay
          // missing in appliedContext until the new preview has been decoded.
          const sameDimensions = canonical(after.binding.dimensionsMm) === canonical(chosen.context.binding.dimensionsMm)
            || after.binding.dimensionsMm == null;
          if (after.originalFile === chosen.context.originalFile && after.effectiveFile === chosen.file
            && sameDimensions && canonical(automaticCommitBinding(after.binding)) === canonical(automaticCommitBinding(chosen.context.binding))) appliedContext = after.binding;
        } catch { /* A changed owner cannot lend authority to this application. */ }
        if (appliedContext) {
          automaticReceipts.add(receipt);
          const applied = freeze({ originalFile: chosen.context.originalFile,
            baseFile: chosen.publicProposal.baseFile, baseReceipt, effectiveFile: chosen.file, originalEquivalent,
            receipt, context: receipt.context, appliedContext, productionReady: false });
          appliedPreparations.set(chosen.file, applied); appliedReceipts.set(receipt, applied);
        }
      }
      if (receipt.consent?.action === 'use_this_version' && chosen.file !== chosen.context.originalFile) {
        acceptedPreparations.set(chosen.file, freeze({ originalFile: chosen.context.originalFile, effectiveFile: chosen.file,
          receipt, context: receipt.context, operations: receipt.transformations, productionReady: false }));
      }
      if (!application || automaticReceipts.has(receipt)) rememberAcceptedPalette(chosen.context.originalFile, chosen.file, receipt);
      return receipt;
    }
    const accept = displayed => apply(displayed);
    const applyCompatible = (displayed, options = {}) => {
      if (!plain(options)) throw fail('AUTOMATIC_APPLICATION_UNSAFE', 'La préparation technique nécessite une analyse vérifiée.');
      return apply(displayed, options);
    };
    function snapshot() {
      ensureAlive();
      if (proposal) { try { current(proposal.context); } catch { clear('stale'); } }
      return freeze({ status, error: copy(error), proposal: proposal ? { proposalId: proposal.manifest.proposalId,
        proposalSha256: proposal.proposalSha256, manifest: proposal.manifest } : null, productionReady: false });
    }
    return Object.freeze({ propose, accept, applyCompatible, snapshot,
      cancel() { ensureAlive(); clear('cancelled'); }, invalidate() { ensureAlive(); clear('stale'); },
      destroy() { if (!disposed) { clear('closed'); disposed = true; } } });
  }
  // This is local knowledge about exact accepted file bytes, not approval of
  // another selection or a supplier/checkout authorization. File copies never
  // inherit it; consumers must still bind the current original and effective file.
  function getAcceptedPalette(file) {
    return file instanceof File ? acceptedPalettes.get(file) || null : null;
  }
  // Acceptance describes these immutable bytes and their captured context.
  // The consumer must compare its current product/variant/zone/technique and
  // capability revision before using this evidence for a local preview. This
  // is never an analysis, a current price, or authorization for production.
  function getAcceptedPreparation(file) {
    return file instanceof File ? acceptedPreparations.get(file) || null : null;
  }
  function getAppliedPreparation(file, receipt = null) {
    const applied = receipt ? appliedReceipts.get(receipt) : file instanceof File && appliedPreparations.get(file);
    return applied?.effectiveFile === file ? applied : null;
  }
  function getAutomaticOriginal(file) {
    return file instanceof File ? automaticOriginals.get(file) || null : null;
  }
  function rememberAcceptedPalette(originalFile, effectiveFile, receipt) {
    if (effectiveFile === originalFile || receipt.consent?.action !== 'use_this_version' && receipt.application?.mode !== 'automatic_compatible') return;
    const operation = receipt.transformations.slice().reverse().find(item =>
      item.type === 'reduce_palette' || item.type === 'replace_or_merge_colors');
    const palette = operation?.palette;
    const maxColors = operation?.type === 'replace_or_merge_colors' && receipt.application?.mode !== 'automatic_compatible'
      ? MAX_EDITABLE_COLORS : MAX_COLORS;
    if (!Array.isArray(palette) || palette.length < 1 || palette.length > maxColors
      || !palette.every(color => typeof color === 'string' && /^#[a-f\d]{6}$/i.test(color))
      || new Set(palette.map(color => color.toLowerCase())).size !== palette.length) return;
    acceptedPalettes.set(effectiveFile, freeze({ originalFile, effectiveFile, palette: palette.slice(),
      ...(typeof operation.requiresInkReview === 'boolean' ? { requiresInkReview: operation.requiresInkReview } : {}),
      ...(typeof operation.inkSeparationVerified === 'boolean' ? { inkSeparationVerified: operation.inkSeparationVerified } : {}),
      ...(typeof operation.inkReviewReason === 'string' ? { inkReviewReason: operation.inkReviewReason } : {}),
      colorCount: palette.length, receipt, automatic: receipt.application?.mode === 'automatic_compatible', productionReady: false }));
  }
  // The durable journal rehashes bytes and timestamps consent itself. Do not
  // send client hashes/authority flags as if they were verified server facts.
  function toDossierAdaptation(receipt) {
    if (!receipt || !acceptedReceipts.has(receipt)) throw fail('ACCEPTED_RECEIPT_REQUIRED', 'Acceptez la proposition exacte avant de préparer son enregistrement.');
    const operations = receipt.transformations.map(operation => {
      const result = copy(operation);
      if (result.mapping) result.mapping = Object.entries(result.mapping).map(([from, to]) => ({ from, to }));
      return result;
    });
    if (receipt.warnings.length) operations.push({ type: 'review_limitations', warnings: receipt.warnings.slice() });
    return freeze({ accepted: true, engine: 'artwork-image-preparation', version: VERSION,
      base: receipt.base.sha256 === receipt.original.sha256 ? 'original' : 'effective', operations });
  }
  function toDossierApplication(receipt) {
    if (!receipt || !automaticReceipts.has(receipt)) throw fail('AUTOMATIC_RECEIPT_REQUIRED', 'La préparation technique exacte est requise.');
    const palette = receipt.application.palette;
    return freeze({ accepted: false, automatic: true, selection: 'technique', engine: 'artwork-image-preparation', version: VERSION,
      base: receipt.base.sha256 === receipt.original.sha256 ? 'original' : 'effective', operations: [{
        type: 'preserve_compatible_palette', automatic: true, sourcePalette: palette.slice(), palette: palette.slice(),
        maxColors: receipt.application.maxColors, changedPixelRatio: 0, background: 'keep', jpegEdgeCleanup: false,
        classification: { kind: 'flat_colors', gradients: 'absent' }, alphaPreserved: true,
      }] });
  }
  window.TodayMerchArtworkImagePreparation = Object.freeze({ VERSION, createSession, prewarm, analyzeCompatible, toDossierAdaptation, toDossierApplication, getAcceptedPalette, getAcceptedPreparation, getAppliedPreparation, getAutomaticOriginal });
})();
